import React, { useContext, useEffect, useState } from 'react';
import { AppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';
import { createApplication, createInquiry, createServiceApplication, validateDocument } from '../api/client';
import { formatPricePKR } from '../utils/catalog';
import { APPLICATION_LABELS, SERVICE_FORMS } from '../utils/serviceApplications';
import SlotPicker from './SlotPicker';
import SearchableSelect from './SearchableSelect';

const emptyForm = {
  customer_name: '',
  phone: '',
  email: '',
  message: '',
  city: '',
  preferred_date: '',
  preferred_time: '',
  notes: '',
  employment_type: 'salaried',
  monthly_income: '',
  down_payment: '',
  tenure_months: '36',
  finance_plan: 'Diminishing Musharakah',
  coverage: 'comprehensive',
  service_type: 'general',
};

const FormModal = () => {
  const { formModal, closeFormModal, showToast } = useContext(AppContext);
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [files, setFiles] = useState({});
  // per document: undefined | 'validating' | 'valid' | 'error', plus the message to show
  const [fileStatus, setFileStatus] = useState({});
  const [fileNotes, setFileNotes] = useState({});

  useEffect(() => {
    if (!formModal.isOpen) return;
    setForm({
      ...emptyForm,
      customer_name: user?.name || '',
      phone: user?.phone || '',
      email: user?.email || '',
    });
    setFiles({});
    setFileStatus({});
    setFileNotes({});
  }, [formModal.isOpen, formModal.applicationType, user]);

  if (!formModal.isOpen) return null;

  const vehicle = formModal.vehicle;
  const isInquiry = formModal.mode === 'inquiry';
  const applicationType = formModal.applicationType || 'testdrive';
  const isTestDrive = !isInquiry && applicationType === 'testdrive';
  const serviceSpec = SERVICE_FORMS[applicationType];
  const isService = !isInquiry && Boolean(serviceSpec);
  const phoneLocked = Boolean(user?.phone);
  const priceLabel = formatPricePKR(vehicle?.base_price_pkr || vehicle?.price);
  const isUsed = (vehicle?.condition || '').toLowerCase() === 'used';

  const onChange = (e) => setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  const onRemoveFile = (key, e) => {
    if (e) e.stopPropagation();
    setFiles((prev) => ({ ...prev, [key]: null }));
    setFileStatus((prev) => ({ ...prev, [key]: undefined }));
    setFileNotes((prev) => ({ ...prev, [key]: '' }));
  };
  const onFile = async (key, file) => {
    setFileNotes((prev) => ({ ...prev, [key]: '' }));
    if (!file) {
      setFiles((prev) => ({ ...prev, [key]: null }));
      setFileStatus((prev) => ({ ...prev, [key]: undefined }));
      return;
    }
    setFileStatus((prev) => ({ ...prev, [key]: 'validating' }));
    setFiles((prev) => ({ ...prev, [key]: null }));
    try {
      const verdict = await validateDocument(key, file);
      setFiles((prev) => ({ ...prev, [key]: file }));
      setFileStatus((prev) => ({ ...prev, [key]: 'valid' }));
      // warn mode: keep the file but show why it looks wrong
      if (verdict?.warning) setFileNotes((prev) => ({ ...prev, [key]: verdict.message || '' }));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      // Only a 422 means "wrong document". Any other failure must not block the applicant;
      // the server checks again on submit.
      if (status === 422 && typeof detail === 'string') {
        setFileStatus((prev) => ({ ...prev, [key]: 'error' }));
        setFileNotes((prev) => ({ ...prev, [key]: detail }));
      } else {
        setFiles((prev) => ({ ...prev, [key]: file }));
        setFileStatus((prev) => ({ ...prev, [key]: undefined }));
      }
    }
  };

  const vehicleLabel =
    vehicle?.variant && !vehicle?.name?.toLowerCase().includes(vehicle.variant.toLowerCase())
      ? `${vehicle.name} ${vehicle.variant}`.trim()
      : vehicle?.name;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fullVehicleName = vehicleLabel;
      const needsSlot = ['testdrive', 'service', 'inspection'].includes(applicationType);
      if (!isInquiry && needsSlot && (!form.preferred_date || !form.preferred_time)) {
        showToast('Choose an open date and time');
        setSubmitting(false);
        return;
      }
      if (isInquiry) {
        await createInquiry({
          customer_name: form.customer_name,
          phone: form.phone,
          email: form.email || undefined,
          vehicle_key: vehicle?.key,
          vehicle_name: fullVehicleName,
          source: formModal.source,
          message: form.message || undefined,
          inquiry_type: 'info',
        });
        showToast('Info request submitted');
      } else if (isTestDrive) {
        await createApplication({
          customer_name: user?.name || form.customer_name,
          phone: phoneLocked ? user.phone : form.phone,
          email: user?.email || undefined,
          vehicle_key: vehicle?.key,
          vehicle_name: fullVehicleName,
          source: formModal.source,
          application_type: 'testdrive',
          city: form.city || undefined,
          preferred_date: form.preferred_date || undefined,
          preferred_time: form.preferred_time || undefined,
          notes: form.notes || undefined,
        });
        showToast('Test drive request submitted');
      } else {
        for (const doc of serviceSpec.docs) {
          if (doc.required && !files[doc.key]) {
            showToast(`${doc.label} is required`);
            setSubmitting(false);
            return;
          }
        }
        await createServiceApplication({
          application_type: applicationType,
          vehicle_key: vehicle?.key,
          vehicle_name: fullVehicleName,
          source: formModal.source,
          city: form.city,
          preferred_date: form.preferred_date,
          preferred_time: form.preferred_time,
          phone: phoneLocked ? user.phone : form.phone,
          notes: form.notes,
          employment_type: form.employment_type,
          monthly_income: form.monthly_income,
          down_payment: form.down_payment,
          tenure_months: form.tenure_months,
          finance_plan: form.finance_plan,
          coverage: form.coverage,
          service_type: form.service_type,
          ...files,
        });
        showToast(`${APPLICATION_LABELS[applicationType] || 'Application'} submitted`);
      }
      closeFormModal();
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      showToast(typeof detail === 'string' ? detail : 'Could not submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay active" onClick={closeFormModal} role="presentation">
      <div className="modal-box form-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
        <button type="button" className="close-btn" onClick={closeFormModal} aria-label="Close">
          ✕ Close
        </button>
        <h2>{formModal.title}</h2>
        {formModal.meta && <p className="modal-meta">{formModal.meta}</p>}
        {vehicleLabel && <p className="form-appliance-tag">{vehicleLabel}</p>}
        {isService && <p className="form-help">Price: {priceLabel}</p>}
        {applicationType === 'history' && (
          <p className="form-help">
            {isUsed
              ? 'This is a used car. Tell us what history you need.'
              : 'This is a new car, so there is no prior-owner history. You can still request the specification pack.'}
          </p>
        )}
        {applicationType === 'protection' && (
          <p className="form-help">We contact you about an inspection before you pay. This is not an escrow service.</p>
        )}
        <form className="request-form" onSubmit={handleSubmit}>
          {!isInquiry && (
            <>
              <label>
                Full name
                <input name="customer_name" value={user?.name || ''} readOnly />
              </label>
              <label>
                Email
                <input name="email" type="email" value={user?.email || ''} readOnly />
              </label>
              <label>
                Phone {phoneLocked ? '' : '*'}
                <input
                  name="phone"
                  required={!phoneLocked}
                  value={phoneLocked ? user.phone : form.phone}
                  onChange={onChange}
                  readOnly={phoneLocked}
                />
              </label>
            </>
          )}
          {isInquiry && (
            <>
              <label>
                Full name *
                <input name="customer_name" required value={form.customer_name} onChange={onChange} />
              </label>
              <label>
                Phone *
                <input name="phone" required value={form.phone} onChange={onChange} />
              </label>
              <label>
                Email
                <input name="email" type="email" value={form.email} onChange={onChange} />
              </label>
              <label>
                Your question
                <textarea name="message" rows={3} value={form.message} onChange={onChange} />
              </label>
            </>
          )}
          {isTestDrive && (
            <>
              <label>
                City
                <input name="city" value={form.city} onChange={onChange} placeholder="Karachi, Lahore…" />
              </label>
              <SlotPicker
                serviceType="testdrive"
                date={form.preferred_date}
                time={form.preferred_time}
                onChange={({ date, time }) => setForm((prev) => ({ ...prev, preferred_date: date, preferred_time: time }))}
              />
              <label>
                Notes
                <textarea name="notes" rows={3} value={form.notes} onChange={onChange} />
              </label>
            </>
          )}
          {applicationType === 'finance' && (
            <>
              <label>
                City *
                <input name="city" required value={form.city} onChange={onChange} placeholder="Karachi, Lahore…" />
              </label>
              <label>
                Employment type *
                <SearchableSelect
                  name="employment_type"
                  value={form.employment_type}
                  onChange={onChange}
                  options={[
                    { value: 'salaried', label: 'Salaried', icon: '💼' },
                    { value: 'self_employed', label: 'Self-employed', icon: '💻' },
                    { value: 'business', label: 'Business', icon: '🏢' },
                  ]}
                  placeholder="Select employment type..."
                />
              </label>
              <label>
                Monthly income (PKR) *
                <input name="monthly_income" required inputMode="decimal" value={form.monthly_income} onChange={onChange} />
              </label>
              <label>
                Down payment (PKR) *
                <input name="down_payment" required inputMode="decimal" value={form.down_payment} onChange={onChange} />
              </label>
              <label>
                Tenure *
                <SearchableSelect
                  name="tenure_months"
                  value={form.tenure_months}
                  onChange={onChange}
                  options={[
                    { value: '12', label: '12 months', icon: '📅' },
                    { value: '24', label: '24 months', icon: '📅' },
                    { value: '36', label: '36 months', icon: '📅' },
                    { value: '48', label: '48 months', icon: '📅' },
                    { value: '60', label: '60 months', icon: '📅' },
                  ]}
                  placeholder="Select tenure..."
                />
              </label>
              <label>
                Plan *
                <SearchableSelect
                  name="finance_plan"
                  value={form.finance_plan}
                  onChange={onChange}
                  options={[
                    { value: 'Ijarah', label: 'Ijarah (Auto Lease)', icon: '📜' },
                    { value: 'Diminishing Musharakah', label: 'Diminishing Musharakah', icon: '🤝' },
                  ]}
                  placeholder="Select finance plan..."
                />
              </label>
            </>
          )}
          {applicationType === 'insurance' && (
            <>
              <label>
                Coverage *
                <SearchableSelect
                  name="coverage"
                  value={form.coverage}
                  onChange={onChange}
                  options={[
                    { value: 'comprehensive', label: 'Comprehensive', icon: '🛡️' },
                    { value: 'third_party', label: 'Third-party', icon: '📄' },
                  ]}
                  placeholder="Select coverage..."
                />
              </label>
              <label>
                City *
                <input name="city" required value={form.city} onChange={onChange} />
              </label>
            </>
          )}
          {applicationType === 'service' && (
            <>
              <label>
                Service *
                <SearchableSelect
                  name="service_type"
                  value={form.service_type}
                  onChange={onChange}
                  options={[
                    { value: 'general', label: 'General service', icon: '🔧' },
                    { value: 'oil', label: 'Oil change', icon: '🛢️' },
                    { value: 'brakes', label: 'Brakes', icon: '🛑' },
                    { value: 'ac', label: 'Air Conditioning (AC)', icon: '❄️' },
                    { value: 'tires', label: 'Tires & Alignment', icon: '🛞' },
                  ]}
                  placeholder="Select service..."
                />
              </label>
              <label>
                City *
                <input name="city" required value={form.city} onChange={onChange} />
              </label>
              <SlotPicker
                serviceType="service"
                date={form.preferred_date}
                time={form.preferred_time}
                onChange={({ date, time }) => setForm((prev) => ({ ...prev, preferred_date: date, preferred_time: time }))}
              />
            </>
          )}
          {applicationType === 'inspection' && (
            <>
              <label>
                City *
                <input name="city" required value={form.city} onChange={onChange} />
              </label>
              <SlotPicker
                serviceType="inspection"
                date={form.preferred_date}
                time={form.preferred_time}
                onChange={({ date, time }) => setForm((prev) => ({ ...prev, preferred_date: date, preferred_time: time }))}
              />
              <label>
                Address *
                <textarea name="notes" required rows={3} value={form.notes} onChange={onChange} />
              </label>
            </>
          )}
          {applicationType === 'verified' && (
            <label>
              What should we check? *
              <textarea name="notes" required rows={3} value={form.notes} onChange={onChange} />
            </label>
          )}
          {applicationType === 'history' && (
            <label>
              Request *
              <textarea name="notes" required rows={3} value={form.notes} onChange={onChange} />
            </label>
          )}
          {applicationType === 'protection' && (
            <>
              <label>
                City
                <input name="city" value={form.city} onChange={onChange} />
              </label>
              <label>
                Notes *
                <textarea name="notes" required rows={3} value={form.notes} onChange={onChange} />
              </label>
            </>
          )}
          {isService &&
            serviceSpec.docs.map((doc) => {
              const status = fileStatus[doc.key];
              const file = files[doc.key];
              const note = fileNotes[doc.key];
              const isValidating = status === 'validating';
              const isValid = status === 'valid';
              const isError = status === 'error';
              return (
                <div key={doc.key} className="file-field">
                  <label>
                    {doc.label}
                    {doc.required ? ' *' : ''}
                  </label>
                  <div className={`file-picker${file ? ' has-file' : ''}${isValidating ? ' is-validating' : ''}${isValid ? ' is-valid' : ''}${isError ? ' has-error' : ''}`}>
                    {!file && !isValidating && (
                      <input
                        type="file"
                        accept={doc.accept}
                        aria-label={doc.label}
                        onChange={(e) => {
                          const picked = e.target.files?.[0] || null;
                          e.target.value = '';
                          onFile(doc.key, picked);
                        }}
                      />
                    )}
                    {isValidating ? (
                      <>
                        <i className="fas fa-spinner fa-spin" style={{ color: '#0284c7' }} />
                        <span className="file-picker-name" style={{ color: '#0369a1' }}>Checking document…</span>
                      </>
                    ) : file ? (
                      <>
                        <i className="fas fa-check-circle" style={{ color: '#16a34a' }} />
                        <span className="file-picker-name">{file.name}</span>
                        <span className="doc-badge-verified">Verified</span>
                        <button
                          type="button"
                          className="file-picker-remove"
                          onClick={(e) => onRemoveFile(doc.key, e)}
                          title="Remove file"
                        >
                          ×
                        </button>
                      </>
                    ) : (
                      <>
                        <span className="file-picker-btn">Choose file</span>
                        <span className="file-picker-name">No file chosen</span>
                      </>
                    )}
                  </div>
                  {note ? (
                    <span className={`file-picker-hint ${isError ? 'file-picker-error' : 'file-picker-warning'}`} role="alert">
                      <i className={`fas ${isError ? 'fa-circle-exclamation' : 'fa-triangle-exclamation'}`} style={{ marginRight: 4 }} />
                      {note}
                    </span>
                  ) : (
                    <span className="file-picker-hint">JPG, PNG, WEBP, or PDF. Max 5 MB.</span>
                  )}
                </div>
              );
            })}
          <button
            type="submit"
            className="btn-submit"
            disabled={submitting || Object.values(fileStatus).includes('validating')}
          >
            {submitting ? 'Submitting…' : isInquiry ? 'Send request' : isTestDrive ? 'Book test drive' : 'Submit application'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default FormModal;
